import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../../../components/ui/Icons';
import { useReveal, useStagger } from '../hooks/useScrollReveal';

interface Props { onEnterApp: () => void; }

const plans = [
  { name: 'Free', price: '$0', period: '/mo', desc: 'Explore what Neura can do.', features: ['100 starter credits', '5 integrations', 'Community support'], cta: 'Get started', pop: false },
  { name: 'Pro', price: '$19', period: '/mo', desc: 'For professionals who move fast.', features: ['2,000 credits/mo', '+1,000 welcome bonus', 'Unlimited integrations', 'Priority support', 'Advanced workflows'], cta: 'Start Pro', pop: true, badge: 'Popular' },
  { name: 'Premium', price: '$199', period: '/mo', desc: 'For teams that need scale.', features: ['25,000 credits/mo', 'Dedicated manager', 'Custom API access', 'SSO & audit logs', 'SLA guarantee'], cta: 'Contact sales', pop: false },
];

const faqs = [
  { q: 'How do you access my data?', a: 'You connect via Google OAuth with minimal permissions. We never store raw credentials and enforce strict security checks.' },
  { q: 'Can Neura send messages without permission?', a: 'No. Every outgoing action requires your explicit approval. You can configure trust levels per integration.' },
  { q: 'Is my data stored or sold?', a: 'Never. We only log action trails for your review. All tokens are encrypted end-to-end.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Month-to-month, no contracts. Cancel from your settings — no questions asked.' },
  { q: 'What happens when I run out of credits?', a: 'You\'ll get a heads-up before hitting your limit. Upgrade, buy more, or wait for the monthly reset.' },
];

const LandingPricing: React.FC<Props> = ({ onEnterApp }) => {
  const navigate = useNavigate();
  const ref = useReveal();
  const cardsRef = useStagger(140);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section id="pricing" className="relative py-10 md:py-14 bg-white overflow-hidden scroll-mt-20">
      <div className="section-line" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 pt-2">
        {/* Header */}
        <div className="text-center max-w-xl mx-auto mb-16 sr">
          <p className="text-[11px] font-semibold text-black/30 tracking-[0.2em] uppercase mb-4">Pricing</p>
          <h2 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.04em] text-black leading-[1.1] mb-3">
            Simple pricing
          </h2>
          <p className="text-[17px] text-black/40 font-normal">Start free. Scale when ready.</p>
        </div>

        {/* Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-16 md:mb-20">
          {plans.map((p, i) => (
            <div
              key={i}
              className={`sr-scale delay-${i + 1} relative bg-white rounded-2xl p-7 border flex flex-col hover-lift ${
                p.pop ? 'border-black/[0.1] shadow-[0_4px_40px_-10px_rgba(0,0,0,0.08)]' : 'border-black/[0.05]'
              }`}
            >
              {p.badge && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-black text-white text-[9px] font-bold px-3.5 py-1 rounded-full uppercase tracking-[0.15em] shadow-md">
                  {p.badge}
                </span>
              )}
              <h3 className="text-[20px] md:text-[32px] font-medium text-black mb-3">{p.name}</h3>
              <div className="flex items-baseline gap-0.5 mb-1">
                <span className="text-[42px] font-semibold text-black tracking-tighter leading-none">{p.price}</span>
                <span className="text-[14px] text-black/30 font-medium">{p.period}</span>
              </div>
              <p className="text-[13px] text-black/35 font-normal mb-6">{p.desc}</p>

              <ul className="space-y-2.5 mb-7 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-[13px] text-black/50 font-normal">
                    <Icons.Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${p.pop ? 'text-black' : 'text-black/20'}`} />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={p.pop ? () => navigate('/pricing') : onEnterApp}
                className={`w-full py-3 rounded-xl text-[13px] font-semibold transition-all duration-300 btn-primary ${
                  p.pop ? 'bg-black text-white' : 'bg-white text-black border border-black/[0.08] hover:border-black/[0.15]'
                }`}
              >
                {p.cta}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h3 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.03em] text-black mb-8 text-center leading-[1.1]">
            Questions & answers
          </h3>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <div
                key={i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="border border-black/[0.05] rounded-xl bg-white overflow-hidden hover:border-black/[0.08] transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between px-5 py-4">
                  <h4 className="text-[14px] font-semibold text-black pr-4">{f.q}</h4>
                  <div className={`w-5 h-5 rounded-full bg-black/[0.04] flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}>
                    <Icons.ChevronDown className="w-3 h-3 text-black/30" />
                  </div>
                </div>
                <div className={`faq-answer ${openFaq === i ? 'open' : ''}`}>
                  <div>
                    <div className="px-5 pb-4">
                      <p className="text-[13px] text-black/40 font-normal leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingPricing;
